import { gql } from "lib/apolloClient";
import {
  organizationAttendanceOptionFragment,
  curriculumProgramFragment,
  studentsAlertFragment,
} from "modules/CommonFragments";
import {
  studentNameFragment,
  staffNameFragment,
  familyMemberNameFragment,
  parentNameFragment,
  unmarkStudentsFragment,
} from "AttendanceDashboard/modules/AttendanceDashboardFragment";

export const getClassesAttendanceDetailsOverviewForEachPeriodQuery = gql`
  query getClassesAttendanceDetailsOverviewForEachPeriod(
    $id: ID!
    $curriculumIds: [ID]
    $periodSetFilters: PeriodSetFilterOptions
    $selectedCoursesFilters: SelectedCoursesFiltersInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $curriculumIds) {
          id
          periodSet(filter: $periodSetFilters) {
            id
            count
            periods {
              id
              label
              type
              isDefault
              selectedCourses(filters: $selectedCoursesFilters) {
                totalCount
                statusWiseCourseAttendanceCount {
                  markedCount
                  notMarkedCount
                  partiallyMarkedCount
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getClassesAttendanceDetailsForEachPeriodQuery = gql`
  query getClassesAttendanceDetailsForEachPeriod(
    $id: ID!
    $coursesFilters: CourseFilterInput
    $periodsFilters: PeriodFilterOptions
    $courseAttendanceV2Filters: CourseAttendanceFilters
    $academicYearId: ID
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $staffFilters: ACLEnabledUsersForCourseFilterInput
    $skipStaffList: Boolean!
    $isToddleV2TimetableEnabled: Boolean!
    $collaboratorFilters: CollaboratorFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(
          filters: $coursesFilters
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              collaborator: allCollaborators(filters: $collaboratorFilters) {
                edges {
                  node {
                    id
                    firstName
                    lastName
                    profileImage
                  }
                }
              }
              enabledUsersV2(
                filters: $staffFilters
                academicYearId: $academicYearId
              ) @skip(if: $skipStaffList) {
                edges {
                  node {
                    id
                  }
                }
              }
              id
              title
              isHomeroom
              grades {
                id
              }
              primaryGrade {
                id
              }
              allStudents {
                totalCount
              }
              periodsV3(filters: $periodsFilters)
                @skip(if: $isToddleV2TimetableEnabled) {
                edges {
                  node {
                    isSelected
                    rotationDay {
                      id
                      label
                    }
                    period {
                      id
                    }
                    day
                    subject
                    location
                    startTime
                    endTime
                    teachers {
                      id
                      email
                      ...staffNameFields
                      profileImage
                      isArchived
                      isArchivedV2(filters: { academicYearId: $academicYearId })
                    }
                  }
                }
              }
              periodsV4(filters: $periodsFilters)
                @include(if: $isToddleV2TimetableEnabled) {
                edges {
                  node {
                    isSelected
                    rotationEntity {
                      id
                      label
                    }
                    day
                    subject
                    location
                    startTime
                    endTime
                    timetable {
                      id
                      label
                    }
                    teachers {
                      type
                      staff {
                        id
                        email
                        firstName
                        lastName
                        middleName
                        profileImage
                        isArchived
                        isArchivedV2
                      }
                    }
                  }
                }
              }
              attendanceV2(filters: $courseAttendanceV2Filters) {
                unmarkedStudents {
                  ...unmarkStudentsDetails
                }
                attendanceMarkedStatus
                attendanceReminderInfo {
                  createdAt
                }
                summary {
                  totalCount
                  value {
                    id
                    label
                  }
                  period {
                    id
                    label
                  }
                }
                edgeInfo {
                  totalCount
                  attendance {
                    createdBy {
                      id
                      profileImage
                      ...staffNameFields
                      type
                    }
                    updatedBy {
                      id
                      profileImage
                      ...staffNameFields
                      type
                    }
                  }
                }
                categorySummary {
                  count
                  category {
                    id
                    label
                  }
                  optionItems {
                    count
                    option {
                      id
                      label
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${staffNameFragment.staffNameFields}
  ${unmarkStudentsFragment.unmarkStudentsDetails}
`;

export const getCurriculumProgramsAttendanceOptionsQuery = gql`
  query getCurriculumProgramsAttendanceOptions(
    $id: ID!
    $selectedCurriculumId: [ID]
    $attendanceOptionsFilter: AttendanceOptionsSetFilterOptions
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $selectedCurriculumId) {
          id
          attendanceOptionSet(filter: $attendanceOptionsFilter) {
            id
            attendanceOptions {
              ...organizationAttendanceOptionItem
            }
          }
        }
      }
    }
  }
  ${organizationAttendanceOptionFragment.organizationAttendanceOption}
`;

// TODO: use attendance option fragment once backend exposes proper data for attendance option
export const getOrganizationMultiCurriculumAttendanceOptionsQuery = gql`
  query getOrganizationMultiCurriculumAttendanceOptions(
    $id: ID!
    $organizationAttendanceFilters: OrganizationAttendanceFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        attendanceV2(filters: $organizationAttendanceFilters) {
          attendanceOptionSet {
            id
            attendanceOptions {
              curriculumProgramSettings {
                settings {
                  sendEmail
                  sendSMS
                  value
                }
              }
              attendanceOption {
                id
                label
                value
                status
                sequence
                abbreviation
                color
                sendSMS
                sendEmail
                presentInCampus
                attendanceType
                allowedRoles {
                  id
                  label
                }
                attendanceCategory {
                  id
                  color
                }
              }
            }
            attendanceCategories {
              id
              label
              isDefault
              color
              attendanceOptions {
                id
                sequence
                abbreviation
                label
                color
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationMultiCurriculumAttendanceCategoriesQuery = gql`
  query getOrganizationMultiCurriculumAttendanceOptions(
    $id: ID!
    $organizationAttendanceFilters: OrganizationAttendanceFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        attendanceV2(filters: $organizationAttendanceFilters) {
          attendanceOptionSet {
            id
            attendanceCategories {
              id
              label
              color
            }
          }
        }
      }
    }
  }
`;

export const getStudentTabsAttendanceOverviewQuery = gql`
  query getStudentTabsAttendanceOverview(
    $id: ID!
    $studentsFilters: OrgConnectionFilterInput
    $anomalyStudentsFilters: OrgConnectionFilterInput
    $organizationAttendanceV2Filters: OrganizationAttendanceFilters
    $isPeriodAttendanceView: Boolean!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(filters: $studentsFilters) {
          totalCount
        }
        anomalyStudents: students(filters: $anomalyStudentsFilters)
          @include(if: $isPeriodAttendanceView) {
          totalCount
        }
        attendanceV2(filters: $organizationAttendanceV2Filters)
          @include(if: $isPeriodAttendanceView) {
          categorySummary {
            marked
            attendanceCategoryCount {
              count
              category {
                id
                label
              }
            }
          }
          summary {
            marked
            attendanceCount {
              totalCount
              value {
                id
                label
                color
              }
            }
          }
        }
        attendanceV2(filters: $organizationAttendanceV2Filters)
          @skip(if: $isPeriodAttendanceView) {
          percentageSummary
          tiersSummary {
            overallPresenceAvg
            tierSummary {
              tier {
                id
                label
                color
                minPercentage
                maxPercentage
              }
              count
            }
          }
        }
      }
    }
  }
`;

export const getStudentTabsAllAttendanceDetailsQuery = gql`
  query getStudentTabsAllAttendanceDetails(
    $id: ID!
    $studentsFilters: OrgConnectionFilterInput
    $courseFilters: CourseFilter
    $studentExcusalFilters: AttendanceExcusalFilters
    $attendanceV2Filters: StudentAttendanceFilters
    $isPeriodAttendanceView: Boolean!
    $enrolledPeriods: Boolean!
    $enrolledPeriodsV2: Boolean!
    $enrolledPeriodsV3: Boolean!
    $isPercentageAttendanceSummarySelected: Boolean! = false
    $academicYearId: ID
    $first: Int
    $after: String
    $enrolledPeriodsFilters: PeriodFilterOptions
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(
          filters: $studentsFilters
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              ...studentNameFields
              profileImage
              sourceId
              isArchived
              isArchivedV2(filters: { academicYearId: $academicYearId })
              enrollmentDate
              yearGroup {
                id
                name
                grade {
                  id
                }
              }
              familyMembers {
                id
                ...familyMemberNameFields
                profileImage
              }
              courses(filters: $courseFilters) {
                id
                title
                primaryGrade {
                  id
                }
              }
              studentAttendanceExcusals(filters: $studentExcusalFilters) {
                edges {
                  node {
                    id
                    reason
                    durationType
                    periods {
                      id
                    }
                    startDate
                    endDate
                  }
                }
              }
              enrolledPeriods(filters: $enrolledPeriodsFilters)
                @include(if: $enrolledPeriods) {
                id
                period {
                  id
                }
                day
                rotationDay
                isSelected
                course {
                  id
                  title
                }
              }
              enrolledPeriodsV2(filters: $enrolledPeriodsFilters)
                @include(if: $enrolledPeriodsV2) {
                period {
                  id
                }
                day
                isSelected
                course {
                  id
                  title
                }
              }
              enrolledPeriodsV3(filters: $enrolledPeriodsFilters)
                @include(if: $enrolledPeriodsV3) {
                period {
                  id
                  label
                }
                day
                isSelected
                course {
                  id
                  title
                }
              }
              attendanceV2(filters: $attendanceV2Filters) {
                categorySummary {
                  countItems @skip(if: $isPercentageAttendanceSummarySelected) {
                    count
                    category {
                      id
                    }
                    optionItems {
                      count
                      option {
                        id
                      }
                    }
                  }
                  percentageItems
                    @include(if: $isPercentageAttendanceSummarySelected) {
                    percentage
                    category {
                      id
                    }
                    optionItems {
                      percentage
                      option {
                        id
                      }
                    }
                  }
                }
                presenceOverview {
                  totalCount
                  presencePercentage
                  absencePercentage
                  presenceNumber
                  absenceNumber
                  tier {
                    id
                    label
                    color
                  }
                }
                edges @include(if: $isPeriodAttendanceView) {
                  node {
                    id
                    date
                    value {
                      id
                      label
                      abbreviation
                      color
                      sendSMS
                      attendanceCategory {
                        id
                        color
                      }
                    }
                    remark
                    period {
                      id
                      label
                    }
                    course {
                      id
                      title
                    }
                    isHomeroomAttendance
                  }
                }
                statistics
                  @include(if: $isPercentageAttendanceSummarySelected) {
                  categoryItems {
                    item {
                      id
                    }
                    percentage
                  }
                }
                summary @skip(if: $isPercentageAttendanceSummarySelected) {
                  totalCount
                  value {
                    id
                    label
                    status
                  }
                }
              }
              alertTypes {
                ...alertTypeFields
              }
            }
          }
        }
      }
    }
  }
  ${familyMemberNameFragment.familyMemberNameFields}
  ${studentNameFragment.studentNameFields}
  ${studentsAlertFragment}
`;

export const getCurriculumProgramPeriodSetQuery = gql`
  query getCurriculumProgramPeriodSet(
    $id: ID!
    $periodSetFilter: PeriodSetFilterOptions
    $selectedCurriculumId: [ID]
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $selectedCurriculumId) {
          ...curriculumProgramItem
        }
      }
    }
  }
  ${curriculumProgramFragment.periodSet}
`;

export const getOrganizationAttendanceExcusalQuery = gql`
  query getOrganizationAttendanceExcusal(
    $id: ID!
    $filters: AttendanceExcusalsFilter
    $activeExcusalsFilters: AttendanceExcusalsFilter
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $isOngoingExcusalVisible: Boolean!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        allExcusals: attendanceExcusals(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          totalCount
          edges {
            cursor
            node {
              id
              student {
                id
                ...studentNameFields
                pronouns
                sourceId
                yearGroup {
                  id
                  name
                  grade {
                    id
                  }
                }
                courses {
                  id
                  title
                  primaryGrade {
                    id
                  }
                }
                alertTypes {
                  ...alertTypeFields
                }
              }
              durationType
              startDate
              endDate
              reason
              attendanceOption {
                id
                label
                abbreviation
                color
                sendSMS
              }
              createdBy {
                id
                firstName
                lastName
                middleName
                type
              }
              periods {
                id
                label
                startTime
                endTime
                abbreviation
              }
              createdAt
              conversation {
                id
                unreadMessageCount
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
        activeExcusals: attendanceExcusals(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $activeExcusalsFilters
        ) @include(if: $isOngoingExcusalVisible) {
          totalCount
          edges {
            cursor
            node {
              id
              student {
                id
                profileImage
                ...studentNameFields
                sourceId
                yearGroup {
                  id
                  name
                  grade {
                    id
                  }
                }
                courses {
                  id
                  title
                }
              }
              durationType
              startDate
              endDate
              reason
              attendanceOption {
                id
                label
                abbreviation
                color
                sendSMS
              }
              createdBy {
                id
                firstName
                lastName
                middleName
                type
              }
              periods {
                id
                label
                startTime
                endTime
                abbreviation
              }
              createdAt
              conversation {
                id
                unreadMessageCount
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${studentNameFragment.studentNameFields}
  ${studentsAlertFragment}
`;

export const getOrganizationAttendanceExcusalCountQuery = gql`
  query getOrganizationAttendanceExcusalCount(
    $id: ID!
    $newExcusalsFilters: AttendanceExcusalsFilter
    $upcomingAndOngoingExcusalsFilters: AttendanceExcusalsFilter
    $markedExcusalsFilters: AttendanceExcusalsFilter
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        newExcusals: attendanceExcusals(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $newExcusalsFilters
        ) {
          totalCount
        }
        upcomingAndOngoingExcusals: attendanceExcusals(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $upcomingAndOngoingExcusalsFilters
        ) {
          totalCount
        }
        markedExcusals: attendanceExcusals(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $markedExcusalsFilters
        ) {
          totalCount
        }
      }
    }
  }
`;

export const getPeriodsCountQuery = gql`
  query getPeriodsCount(
    $id: ID!
    $curriculumIds: [ID]
    $periodsSetFilters: PeriodSetFilterOptions
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $curriculumIds) {
          id
          periodSet(filter: $periodsSetFilters) {
            id
            count
          }
        }
      }
    }
  }
`;

export const getStudentExcusalDetailQuery = gql`
  query getStudentExcusalDetail($id: ID!) {
    node(id: $id, type: ATTENDANCE_EXCUSAL) {
      id
      ... on AttendanceExcusal {
        id
        attendanceOption {
          id
          label
        }
        createdAt
      }
    }
  }
`;

export const getHomeroomAttendanceDetailsOverviewQuery = gql`
  query getHomeroomAttendanceDetailsOverview(
    $id: ID!
    $staffFilters: OrgConnectionFilterInput
    $markedAttendanceStaffFilters: OrgConnectionFilterInput
    $notMarkedAttendanceStaffFilters: OrgConnectionFilterInput
    $partiallyMarkedAttendanceStaffFilters: OrgConnectionFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        staff(filters: $staffFilters) {
          totalCount
        }
        markedAttendanceStaff: staff(filters: $markedAttendanceStaffFilters) {
          totalCount
        }
        notMarkedAttendanceStaff: staff(
          filters: $notMarkedAttendanceStaffFilters
        ) {
          totalCount
        }
        partiallyMarkedAttendanceStaff: staff(
          filters: $partiallyMarkedAttendanceStaffFilters
        ) {
          totalCount
        }
      }
    }
  }
`;

export const getHomeroomAttendanceDetailsQuery = gql`
  query getHomeroomAttendanceDetails(
    $id: ID!
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $staffFilters: OrgConnectionFilterInput
    $staffStudentFilters: StaffStudentRelationFilter
    $homeroomAttendanceFilters: HomeroomAttendanceFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        staff(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $staffFilters
        ) {
          totalCount
          edges {
            node {
              id
              ...staffNameFields
              profileImage
              studentsRelation(filters: $staffStudentFilters) {
                totalCount
              }
              homeroomAttendance(filters: $homeroomAttendanceFilters) {
                unmarkedStudents {
                  ...unmarkStudentsDetails
                }
                attendanceMarkedStatus
                attendanceReminderInfo {
                  createdAt
                }
                summary {
                  totalCount
                  value {
                    id
                  }
                }
                categorySummary {
                  count
                  category {
                    id
                  }
                  optionItems {
                    count
                    option {
                      id
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${staffNameFragment.staffNameFields}
  ${unmarkStudentsFragment.unmarkStudentsDetails}
`;

export const getCourseSubjectLevelTagsQuery = gql`
  query getCourseSubjectLevelTags($id: ID!) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        genericTags(filters: { types: SUBJECT_LEVEL }) {
          id
          label
          type
        }
      }
    }
  }
`;

export const getCurriculumDefaultPeriodQuery = gql`
  query getCurriculumDefaultPeriod(
    $id: ID!
    $curriculumIds: [ID]
    $periodSetFilters: PeriodSetFilterOptions
    $periodFilters: PeriodFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $curriculumIds) {
          id
          periodSet(filter: $periodSetFilters) {
            id
            periods(filters: $periodFilters) {
              id
              label
            }
          }
        }
      }
    }
  }
`;

export const getCalendarEventItemsQuery = gql`
  query getCalendarEventItems(
    $rangeBeginStartDate: String!
    $rangeEndStartDate: String!
    $filters: CalendarEventFilter
  ) {
    platform {
      calendarEventItems(
        rangeBeginStartDate: $rangeBeginStartDate
        rangeEndStartDate: $rangeEndStartDate
        filters: $filters
      ) {
        id
        start: startDateTime
        end: endDateTime
        title: label
        associatedEntities(filters: { entityTypes: ["GRADE"] }) {
          id
        }
      }
    }
  }
`;

export const getClassesAttendanceStatusOverviewQuery = gql`
  query getClassesAttendanceStatusOverview(
    $id: ID!
    $organizationAttendanceFilters: OrganizationAttendanceFilters
    $courseStatusOverviewFilters: OrganizationCourseStatusOverviewInput!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        attendanceV2(filters: $organizationAttendanceFilters) {
          courseStatusOverview(filters: $courseStatusOverviewFilters) {
            status
            totalCount
          }
        }
      }
    }
  }
`;

export const generateStudentCampusPresenceClassSummaryReportQuery = gql`
  query generateStudentCampusPresenceClassSummaryReport(
    $input: StudentCampusPresenceClassSummaryReportInput!
  ) {
    platform {
      generateStudentCampusPresenceClassSummaryReport(input: $input) {
        isSuccess
        warning
        fileUrl
      }
    }
  }
`;

export const getStudentsForBulkAttendanceMarkingQuery = gql`
  query getStudentsForBulkAttendanceMarking(
    $first: Int
    $after: String
    $filters: StudentBulkAttendanceMarkingFilters!
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    platform {
      getStudentsForBulkAttendanceMarking(
        first: $first
        after: $after
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          cursor
          student {
            id
            ...studentNameFields
            profileImage
            sourceId
            isArchived
            yearGroup {
              id
              name
              grade {
                id
              }
            }
            familyMembers {
              id
              ...familyMemberNameFields
              profileImage
            }
          }
          course {
            id
            title
            primaryGrade {
              id
            }
          }
          period {
            id
            label
            type
            isDefault
            startTime
            endTime
            sequence
          }
          date
          attendanceOption {
            id
            label
            value
            status
            abbreviation
            color
            sendSMS
            sendEmail
            allowedRoles {
              id
              label
            }
            attendanceCategory {
              id
              color
            }
          }
          note
          excusals {
            id
            reason
            durationType
            startDate
            endDate
            attendanceOption {
              id
              label
              abbreviation
              color
            }
            createdBy {
              id
              ...parentNameFields
            }
            periods {
              id
              label
            }
          }
        }
      }
    }
  }
  ${studentNameFragment.studentNameFields}
  ${familyMemberNameFragment.familyMemberNameFields}
  ${parentNameFragment.parentNameFields}
`;

export const getStaffListQuery = gql`
  query getStaffList(
    $organizationId: ID!
    $searchText: String
    $grades: [ID!]
    $courses: [ID!]
    $archivalState: ARCHIVAL_STATE_ENUM
    $curriculumProgramIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(
          filters: {
            searchText: $searchText
            grades: $grades
            courses: $courses
            archivalState: $archivalState
            curriculumProgramIds: $curriculumProgramIds
          }
        ) {
          totalCount
          edges {
            node {
              id
              ...staffNameFields
              profileImage
              email
              roles
              type
              role
              isArchived
              courses {
                id
                title
              }
            }
          }
        }
      }
    }
  }
  ${staffNameFragment.staffNameFields}
`;

export const getStudentTabsBasicDetailsQuery = gql`
  query getStudentTabsBasicDetails(
    $id: ID!
    $studentsFilters: OrgConnectionFilterInput
    $courseFilters: CourseFilter
    $academicYearId: ID
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(
          filters: $studentsFilters
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              ...studentNameFields
              profileImage
              sourceId
              isArchived
              isArchivedV2(filters: { academicYearId: $academicYearId })
              yearGroup {
                id
                name
              }
              familyMembers {
                id
                ...familyMemberNameFields
                profileImage
              }
              courses(filters: $courseFilters) {
                id
                title
                primaryGrade {
                  id
                }
              }
              alertTypes {
                ...alertTypeFields
              }
            }
          }
        }
      }
    }
  }
  ${familyMemberNameFragment.familyMemberNameFields}
  ${studentNameFragment.studentNameFields}
  ${studentsAlertFragment}
`;

export const getStudentTabsAttendanceDetailsQuery = gql`
  query getStudentTabsAttendanceDetails(
    $id: ID!
    $studentsFilters: OrgConnectionFilterInput
    $studentExcusalFilters: AttendanceExcusalFilters
    $attendanceV2Filters: StudentAttendanceFilters
    $isPeriodAttendanceView: Boolean!
    $enrolledPeriods: Boolean!
    $enrolledPeriodsV2: Boolean!
    $enrolledPeriodsV3: Boolean!
    $isPercentageAttendanceSummarySelected: Boolean! = false
    $first: Int
    $after: String
    $enrolledPeriodsFilters: PeriodFilterOptions
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(
          filters: $studentsFilters
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              studentAttendanceExcusals(filters: $studentExcusalFilters) {
                edges {
                  node {
                    id
                    reason
                    durationType
                    periods {
                      id
                    }
                    startDate
                    endDate
                  }
                }
              }
              enrolledPeriods(filters: $enrolledPeriodsFilters)
                @include(if: $enrolledPeriods) {
                id
                period {
                  id
                }
                day
                rotationDay
                isSelected
                course {
                  id
                  title
                }
              }
              enrolledPeriodsV2(filters: $enrolledPeriodsFilters)
                @include(if: $enrolledPeriodsV2) {
                period {
                  id
                }
                day
                isSelected
                course {
                  id
                  title
                }
              }
              enrolledPeriodsV3(filters: $enrolledPeriodsFilters)
                @include(if: $enrolledPeriodsV3) {
                period {
                  id
                  label
                }
                day
                isSelected
                course {
                  id
                  title
                }
              }
              attendanceV2(filters: $attendanceV2Filters) {
                categorySummary {
                  countItems @skip(if: $isPercentageAttendanceSummarySelected) {
                    count
                    category {
                      id
                    }
                    optionItems {
                      count
                      option {
                        id
                      }
                    }
                  }
                  percentageItems
                    @include(if: $isPercentageAttendanceSummarySelected) {
                    percentage
                    category {
                      id
                    }
                    optionItems {
                      percentage
                      option {
                        id
                      }
                    }
                  }
                }
                presenceOverview {
                  totalCount
                  presencePercentage
                  absencePercentage
                  presenceNumber
                  absenceNumber
                  tier {
                    id
                    label
                    color
                  }
                }
                edges @include(if: $isPeriodAttendanceView) {
                  node {
                    id
                    date
                    value {
                      id
                      label
                      abbreviation
                      color
                      sendSMS
                      attendanceCategory {
                        id
                        color
                      }
                    }
                    remark
                    period {
                      id
                      label
                    }
                    course {
                      id
                      title
                    }
                    isHomeroomAttendance
                  }
                }
                statistics
                  @include(if: $isPercentageAttendanceSummarySelected) {
                  categoryItems {
                    item {
                      id
                    }
                    percentage
                  }
                }
                summary @skip(if: $isPercentageAttendanceSummarySelected) {
                  totalCount
                  value {
                    id
                    label
                    status
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
